import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, errMsg, fmtDate } from "../../lib/api";
import { PageHeader } from "../../components/site/Bits";
import { FullLoader } from "../../components/site/Guards";
import { Field } from "../public/Auth";
import { ChangePasswordForm } from "../app/Account";
import { Toggle } from "./kit";

const LABELS = {
  store_name: "Store name", tagline: "Tagline", support_email: "Support email", support_phone: "Support phone", address: "Address",
  show_unavailable: "Show unavailable products", max_price: "Maximum product price (GH₵)",
  section_enabled: "Enable GET PREDICTIONS NOW section", coming_soon_message: "Coming-soon message",
  announcement: "Announcement bar text", show_announcement: "Show announcement bar",
  payment_description_prefix: "Payment description prefix", default_customer_email: "Fallback customer email (required by theteller)", channels_note: "Payment channels note",
};

const SettingsForm = ({ group, values, onSaved }) => {
  const [f, setF] = useState(values);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put(`/admin/settings/${group}`, f); onSaved(group, data.values); toast.success("Settings saved"); } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <form onSubmit={save} className="card-rx space-y-5 p-7" data-testid={`settings-form-${group}`}>
      {Object.entries(f).map(([k, v]) => typeof v === "boolean" ? (
        <Toggle key={k} checked={v} onChange={(nv) => setF({ ...f, [k]: nv })} label={LABELS[k] || k} testId={`setting-${k}`} />
      ) : (
        <Field key={k} label={LABELS[k] || k}>
          <input value={v} type={typeof v === "number" ? "number" : "text"} onChange={(e) => setF({ ...f, [k]: typeof v === "number" ? Number(e.target.value) : e.target.value })} className="input-rx" data-testid={`setting-${k}`} />
        </Field>
      ))}
      <button className="btn-primary" data-testid={`settings-save-${group}`}>Save</button>
    </form>
  );
};

const TellerStatus = ({ t }) => (
  <div className="card-rx space-y-5 p-7" data-testid="teller-status">
    <div className="flex items-center gap-3">
      {t.configured ? <CheckCircle2 className="text-emerald-400" /> : <CircleAlert className="text-amber-400" />}
      <div><p className="font-semibold text-white">theteller gateway</p><p className="text-sm text-slate-400">Mode: <b className="uppercase text-slate-200" data-testid="teller-mode">{t.mode}</b> {t.base_url && `· ${t.base_url}`}</p></div>
    </div>
    <dl className="grid gap-3 text-sm sm:grid-cols-2">
      {[["Merchant ID", t.merchant_id || "Not set"], ["API user", t.api_user || "Not set"], ["API key", t.api_key_set ? "Set (hidden)" : "Not set"], ["Webhook token", t.webhook_token_set ? "Set (hidden)" : "Not set"]].map(([k, v]) => (
        <div key={k} className="rounded-xl bg-white/[0.03] p-3"><dt className="text-xs text-slate-500">{k}</dt><dd className="mt-1 font-mono-rx text-slate-200">{v}</dd></div>
      ))}
    </dl>
    <div className="rounded-xl border border-white/[0.06] p-4 text-sm leading-relaxed text-slate-400">
      Credentials are stored only as server environment variables (<code className="text-emerald-300">TELLER_MODE, TELLER_MERCHANT_ID, TELLER_API_USER, TELLER_API_KEY</code>) and are never sent to the browser. Set <code className="text-emerald-300">TELLER_MODE</code> to <b>mock</b> (simulated), <b>test</b> (theteller sandbox) or <b>live</b> (real payments).
      <br />Callback / webhook URL: <code className="break-all text-emerald-300">{window.location.origin}/api/payments/webhook/theteller</code>
    </div>
  </div>
);

export default function AdminSettings() {
  const [d, setD] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setD(r.data)).catch(() => setFailed(true)); }, []);
  if (failed) return <p className="text-sm text-red-300" data-testid="settings-load-error">Could not load settings. Please refresh the page.</p>;
  if (!d) return <FullLoader />;
  const onSaved = (g, v) => setD({ ...d, values: { ...d.values, [g]: v } });
  const tabs = [["teller", "Teller"], ["store", "Store"], ["product", "Products"], ["prediction", "Predictions"], ["website", "Website"], ["account", "Admin account"]];
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title="Settings" />
      <Tabs defaultValue="teller">
        <TabsList className="no-scrollbar h-auto w-full justify-start gap-1 overflow-x-auto bg-white/[0.03] p-1">
          {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-200" data-testid={`settings-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="teller" className="mt-6 grid gap-6 lg:grid-cols-2"><TellerStatus t={d.teller} /><SettingsForm group="teller" values={d.values.teller} onSaved={onSaved} /></TabsContent>
        {["store", "product", "prediction", "website"].map((g) => <TabsContent key={g} value={g} className="mt-6 max-w-2xl"><SettingsForm group={g} values={d.values[g]} onSaved={onSaved} /></TabsContent>)}
        <TabsContent value="account" className="mt-6 max-w-xl"><ChangePasswordForm /></TabsContent>
      </Tabs>
    </div>
  );
}

export function AdminLogs() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/logs").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);
  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title="Activity logs" text="Latest 100 security and admin events." />
      {!items ? <FullLoader /> : (
        <div className="card-rx divide-y divide-white/[0.04]" data-testid="admin-logs">
          {items.length === 0 && <p className="p-6 text-sm text-slate-500">No activity yet.</p>}
          {items.map((l, i) => (
            <div key={i} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
              <span><span className="font-mono-rx text-emerald-300">{l.action}</span> <span className="text-slate-400">by {l.actor_phone || "system"}</span>{l.target && <span className="text-slate-500"> → {l.target}</span>}</span>
              <span className="text-xs text-slate-500">{fmtDate(l.created_at, true)} · {l.ip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
